import React, {
  createContext,
  useContext,
  useState,
  useEffect,
} from 'react';

const AuthContext = createContext(undefined);

const mockUsers = [
  {
    id: '1',
    email: 'patient@example.com',
    role: 'patient',
    name: 'John Doe',
    profileComplete: true
  },
  {
    id: '2',
    email: 'dr.smith@clinic.com',
    role: 'provider',
    name: 'Dr. Sarah Smith',
    specialty: 'General Psychiatry',
    licenseNumber: 'PSY12345',
    profileComplete: true
  },
  {
    id: '3',
    email: 'facility@sunnymeadows.com',
    role: 'facility',
    name: 'Facility Manager',
    facilityName: 'Sunny Meadows Assisted Living',
    profileComplete: true
  },
  {
    id: '4',
    email: 'admin@clinic.com',
    role: 'admin',
    name: 'Admin User',
    profileComplete: true
  }
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🔍 AuthProvider: Checking for existing session...');

    const savedUser = localStorage.getItem('clinic_user');
    console.log('📁 Found saved user data:', savedUser ? 'Yes' : 'No');

    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        console.log('👤 Parsed user:', parsedUser);
        setUser(parsedUser);
        console.log('✅ User automatically logged in as:', parsedUser.role);
      } catch (error) {
        console.error('❌ Error parsing saved user data:', error);
        localStorage.removeItem('clinic_user');
        console.log('🧹 Removed corrupted user data');
      }
    } else {
      console.log('🚫 No saved user data found');
    }

    setLoading(false);
    console.log('🏁 AuthProvider initialization complete');
  }, []);

  const login = async (email, password) => {
    console.log('🔐 Login attempt for:', email);
    setLoading(true);

    await new Promise(resolve => setTimeout(resolve, 1000));

    const foundUser = mockUsers.find(u => u.email === email);

    if (foundUser && password === 'password123') {
      console.log('✅ Login successful for:', foundUser.role);
      setUser(foundUser);
      localStorage.setItem('clinic_user', JSON.stringify(foundUser));
      setLoading(false);
      return true;
    }

    console.log('❌ Login failed');
    setLoading(false);
    return false;
  };

  const register = async (userData) => {
    console.log('📝 Registration attempt for:', userData.email);
    setLoading(true);

    await new Promise(resolve => setTimeout(resolve, 1000));

    const newUser = {
      id: Date.now().toString(),
      email: userData.email,
      role: userData.role,
      name: userData.name,
      profileComplete: false,
      ...(userData.facilityName && { facilityName: userData.facilityName }),
      ...(userData.specialty && { specialty: userData.specialty }),
      ...(userData.licenseNumber && { licenseNumber: userData.licenseNumber })
    };

    mockUsers.push(newUser);

    console.log('✅ Registration successful for:', newUser.role);
    setUser(newUser);
    localStorage.setItem('clinic_user', JSON.stringify(newUser));
    setLoading(false);
    return true;
  };

  const clearAllData = () => {
    console.log('🧹 MANUAL: Clearing all data...');

    setUser(null);

    localStorage.clear();

    sessionStorage.clear();

    console.log('✅ MANUAL: All data cleared');
  };

  const logout = () => {
    console.log('🚪 Logout initiated...');
    console.log('👤 Current user before logout:', user?.role);

    setUser(null);
    console.log('✅ User state cleared');

    const keys = Object.keys(localStorage);
    console.log('📁 localStorage keys before clearing:', keys);

    localStorage.removeItem('clinic_user');
    localStorage.removeItem('clinic_session');
    localStorage.removeItem('clinic_token');
    localStorage.removeItem('clinic_auth');

    const authKeys = keys.filter(key =>
      key.includes('auth') ||
      key.includes('user') ||
      key.includes('session') ||
      key.includes('token') ||
      key.includes('clinic')
    );

    authKeys.forEach(key => {
      localStorage.removeItem(key);
      console.log('🗑️ Removed localStorage key:', key);
    });

    sessionStorage.clear();
    console.log('🧹 SessionStorage cleared');

    const remainingUser = localStorage.getItem('clinic_user');
    console.log('🔍 User data after clearing:', remainingUser ? 'STILL EXISTS!' : 'Successfully removed');

    console.log('✅ Logout process complete');

    console.log('🔄 Forcing page reload...');
    setTimeout(() => {
      window.location.href = window.location.origin;
    }, 100);
  };

  const updateUser = (userData) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      console.log('📝 User updated:', updatedUser.role);
      setUser(updatedUser);
      localStorage.setItem('clinic_user', JSON.stringify(updatedUser));
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    clearAllData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};